/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Link, Tag } from '@carbon/react';
import { Enterprise, Folders } from '@carbon/icons-react';

import { editIdpProjectVersion } from 'App/Pages/IdpProjectVersions/features/edit';
import { organizationStore, userStore } from 'stores';
import { VersionMenu } from 'App/Pages/IdpProjectVersions/features/view-versions-list/version-menu';
import { DRAFT_VERSION, DraftVersion, VersionsList } from 'components';
// Line removed as it is unused.
import { Tooltip } from 'primitives';

import { useSelectDraftIfSelectedVersionIsDeleted } from './use-select-draft-if-selected-version-is-deleted';

export default function IdpApplicationVersionsList({
  versions,
  reloadVersions,
  selectedMilestoneId,
  setSelectedMilestoneId,
  isDiffingEnabled
}) {
  const processedVersion = versions.map((version) => ({ ...version, id: version.milestoneId ?? version.id }));
  useSelectDraftIfSelectedVersionIsDeleted(processedVersion, selectedMilestoneId, setSelectedMilestoneId);
  const [latestVersion] = processedVersion.sort((a, b) => b.created - a.created);

  const renderVersionMenu = ({ version, setIsEditing }) => {
    const canPublishToOrganization =
      organizationStore.hasElevatedOrganizationPermissions && version.organizationPublic === false;

    return (
      <VersionMenu
        milestoneId={version.id}
        reloadVersions={reloadVersions}
        setIsEditing={setIsEditing}
        canPublishToOrganization={canPublishToOrganization}
        canRestoreAsLatest={latestVersion?.id !== version.id}
      />
    );
  };

  const renderPublishedTag = ({ version }) => (
    <Tooltip title={`Published to ${version.organizationPublic ? 'organization' : 'project'}`}>
      <Tag renderIcon={version.organizationPublic ? Enterprise : Folders} type="high-contrast">
        <span>Published</span>
      </Tag>
    </Tooltip>
  );

  const editVersion = async ({ name, description, version, reloadVersions, setIsEditing }) =>
    await editIdpProjectVersion(version.id, name, reloadVersions, setIsEditing, description);

  return (
    <VersionsList
      versions={processedVersion}
      reloadVersions={reloadVersions}
      selectedVersionId={selectedMilestoneId}
      setSelectedVersionId={(version) => setSelectedMilestoneId(version.id)}
      isDiffingEnabled={isDiffingEnabled}
      currentUserId={userStore.userId}
      renderVersionMenu={renderVersionMenu}
      renderPublishedTag={renderPublishedTag}
      editDialogLabel={<label id="version-tag-label">Version tag</label>}
      editDialogSubtitle={
        <>
          Use versioning to track and review changes across all resources in the IDP application.&nbsp;
          <Link
            href="https://docs.camunda.io/docs/next/components/modeler/web-modeler/idp/idp-unstructured-extraction/#versions"
            target="_blank"
          >
            Learn more
          </Link>
        </>
      }
      onEditVersion={editVersion}
      DraftVersion={
        <DraftVersion
          lastModified={latestVersion?.changed}
          isPrimarySelection={selectedMilestoneId === DRAFT_VERSION.id}
          isSecondarySelection={false}
          setSelectedVersionId={setSelectedMilestoneId}
          onCreateVersion={() => {}}
          VersionCreationModal={null}
        />
      }
    />
  );
}
